import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { formatINR, TRACK_LABELS } from '@/lib/tracks';
import { Check, Sparkles, ShieldCheck, Gift, Zap, Flame } from 'lucide-react';

const FEATURES_BY_PLAN = {
  BOARDS_THEORY: [
    'All Class 11 & 12 Board theory chapters',
    '35 MCQs × 2 sets per chapter',
    'Every practicals chapter — viva Q&A + examiner tips',
    'Derivations checklist + AI report card',
  ],
  JEE: ['Full JEE syllabus (Class 11+12)', 'PYQ + module style MCQs', 'Derivations mastery', 'AI report card'],
  NEET: ['Full NEET syllabus', 'NCERT-line accuracy', 'Diagram-based Bio drills', 'AI report card'],
  BOARDS_QBANK: [
    'Class 12 Q-Bank · 5 subjects',
    'Physics · Chemistry · Maths · Biology · English',
    'Curated Drive PDFs + preview',
    'Already included free with Boards plan',
  ],
  ALL_ACCESS: ['Everything above unlocked', 'Boards + JEE + NEET + Q-Bank', 'Save ₹1,098 vs. individual plans', 'Priority updates'],
};

export default function PricingPage() {
  const nav = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [buyingId, setBuyingId] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/pricing'), api.get('/entitlements')])
      .then(([p, e]) => {
        setPricing(p.data);
        setEntitlements(e.data);
      })
      .catch(() => toast.error('Failed to load pricing'));
  }, []);

  const buy = async (plan_id) => {
    setBuyingId(plan_id);
    try {
      const { data } = await api.post('/orders', { plan_id });
      nav(`/checkout/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create order');
    } finally {
      setBuyingId(null);
    }
  };

  const isActive = (planId) => {
    if (!entitlements) return false;
    if (planId === 'ALL_ACCESS') return entitlements.all_access_active;
    return !!entitlements.plans?.[planId]?.expires_at;
  };

  const plans = pricing?.plans || [];
  const sectionPlans = plans.filter((p) => p.id !== 'ALL_ACCESS');
  const allAccess = plans.find((p) => p.id === 'ALL_ACCESS');

  return (
    <AppShell>
      <div className="space-y-8" data-testid="pricing-page">
        <section>
          <div className="inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-600">
            <Gift className="h-3.5 w-3.5" />
            1 chapter free from every section · No credit card
          </div>
          <h1 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">Pick your plan</h1>
          <p className="mt-1 max-w-xl text-muted-foreground">
            Boards (Theory + Practicals + Q-Bank bonus) · JEE · NEET — ₹999 / year each. Or unlock everything with All-Access for ₹1,899 / year.
          </p>
        </section>

        <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sectionPlans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              features={FEATURES_BY_PLAN[p.id] || []}
              active={isActive(p.id)}
              loading={buyingId === p.id}
              onBuy={() => buy(p.id)}
            />
          ))}
        </section>

        {allAccess && (
          <section>
            <div className="relative overflow-hidden rounded-2xl border border-red-500/40 bg-black text-white shadow-[0_20px_60px_-25px_rgba(239,68,68,0.6)]">
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  backgroundImage:
                    'radial-gradient(700px circle at 90% 20%, rgba(239,68,68,0.35), transparent 55%), radial-gradient(500px circle at 10% 100%, rgba(220,38,38,0.18), transparent 60%)',
                }}
              />
              <div className="relative flex flex-col gap-6 p-6 sm:p-10 md:flex-row md:items-center md:justify-between">
                <div>
                  <Badge className="mb-3 bg-red-600 text-white hover:bg-red-500">
                    {allAccess.badge || 'Best Value'}
                  </Badge>
                  <div className="font-display text-3xl font-semibold sm:text-4xl">All Access</div>
                  <p className="mt-1 max-w-md text-white/70">{allAccess.tagline}</p>
                  <ul className="mt-3 space-y-1 text-sm text-white/80">
                    {(FEATURES_BY_PLAN.ALL_ACCESS || []).map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-red-400" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col items-start gap-3 md:items-end">
                  <div className="font-display text-4xl font-bold">
                    {formatINR(allAccess.price)}
                    <span className="text-base font-normal text-white/60"> / year</span>
                  </div>
                  <Button
                    size="lg"
                    onClick={() => buy('ALL_ACCESS')}
                    disabled={buyingId === 'ALL_ACCESS' || isActive('ALL_ACCESS')}
                    className="h-12 gap-2 bg-red-600 px-6 font-semibold text-white shadow-[0_0_28px_rgba(239,68,68,0.45)] hover:bg-red-500"
                    data-testid="buy-plan-ALL_ACCESS"
                  >
                    <Zap className="h-4 w-4" />
                    {isActive('ALL_ACCESS')
                      ? 'Active'
                      : buyingId === 'ALL_ACCESS'
                      ? 'Creating order…'
                      : 'Buy All Access'}
                  </Button>
                </div>
              </div>
            </div>
          </section>
        )}

        <section>
          <Card>
            <CardContent className="space-y-2 p-5">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                <div className="font-display text-base font-semibold">Payment via GPay / any UPI · QR</div>
              </div>
              <p className="text-sm text-muted-foreground">
                On checkout, scan the QR or tap “Pay via GPay”, complete the transfer, then enter your UPI Transaction Reference (UTR) to unlock instantly.
              </p>
            </CardContent>
          </Card>
        </section>
      </div>
    </AppShell>
  );
}

function PlanCard({ plan, features, active, loading, onBuy }) {
  const includesQbank = plan.id === 'BOARDS_THEORY' || plan.id === 'ALL_ACCESS';
  return (
    <Card
      className={`group relative flex flex-col overflow-hidden transition-shadow hover:shadow-md ${
        plan.id === 'BOARDS_THEORY' ? 'border-red-500/40 shadow-[0_0_0_1px_rgba(239,68,68,0.15)]' : ''
      }`}
    >
      {plan.id === 'BOARDS_THEORY' && (
        <div
          className="flex items-center justify-center gap-1.5 bg-gradient-to-r from-red-600 to-red-500 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white"
          data-testid="qbank-bonus-ribbon"
        >
          <Flame className="h-3.5 w-3.5" />
          Theory + Practicals · Free Q-Bank bonus
        </div>
      )}
      <CardContent className="flex flex-1 flex-col gap-4 p-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-display text-lg font-semibold">{plan.name}</div>
            {includesQbank && plan.id !== 'ALL_ACCESS' && (
              <Badge className="gap-1 bg-red-600 text-white shadow-[0_0_12px_rgba(239,68,68,0.35)]">
                <Flame className="h-3 w-3" />
                Q-Bank bonus
              </Badge>
            )}
          </div>
          <div className="mt-1 min-h-[2.5rem] text-sm text-muted-foreground">{plan.tagline}</div>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="font-display text-3xl font-bold">{formatINR(plan.price)}</span>
          <span className="text-sm text-muted-foreground">/ year</span>
        </div>
        <ul className="flex-1 space-y-1.5 text-sm">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 text-emerald-600" />
              <span>{f}</span>
            </li>
          ))}
          {plan.id === 'BOARDS_THEORY' && (
            <li className="flex items-start gap-2 text-red-500">
              <Flame className="mt-0.5 h-4 w-4" />
              <span className="font-medium">Bonus: full Class 12 Q-Bank unlocked (₹999 value)</span>
            </li>
          )}
          {plan.id === 'BOARDS_QBANK' && (
            <li className="flex items-start gap-2 text-muted-foreground">
              <Gift className="mt-0.5 h-4 w-4" />
              <span>Tip: this is included free with the Boards plan.</span>
            </li>
          )}
        </ul>
        <Button
          className="gap-1.5"
          onClick={onBuy}
          disabled={loading || active}
          data-testid={`buy-plan-${plan.id}`}
        >
          <Sparkles className="h-4 w-4" />
          {active ? 'Active' : loading ? 'Creating order…' : 'Buy plan'}
        </Button>
      </CardContent>
    </Card>
  );
}
